import { get, isEmpty } from "lodash";
import { GetServerSidePropsContext, InferGetServerSidePropsType } from "next";
import Skeleton from "react-loading-skeleton";
import Breadcrumb from "~/components/Breadcrumb";
import Container from "~/components/Container";
import SEOTags from "~/components/SEOTags";
import SkeletonByTimeout from "~/components/Skeletons/SkeletonByTimeout";
import TableOfContent from "~/components/TableOfContent";
import Text from "~/components/Text";
import { PATH } from "~/constants/enum";
import { QUERY_GET_CMS_PAGE } from "~/data/cms/gql";
import { useReplaceCMS } from "~/hooks/useReplaceCMS";
import Layout from "~/layouts/ThemeLayout";
import { initializeSSRApollo } from "~/utils/apollo-client";
import { processCMSContent } from "~/utils/cms-blocks-ssr";
import dynamic from "next/dynamic";
import { useMemo } from "react";

const getText = (domNode: any) => {
  let text = "";
  domNode?.children?.forEach((child: any) => {
    if (child.type === "text") {
      text += child.data;
    } else {
      text += getText(child);
    }
  });
  return text;
};

const CmsPage = ({
  data,
  loading,
  vars = {
    template: "v1",
  },
  components: dynamicComponents = [],
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const cmsPage = get(data, "cmsPage.content", "") as string;

  // Create a map of dynamic components
  const componentMap = useMemo(() => {
    const map: Record<string, any> = {};
    dynamicComponents.forEach((comp: any) => {
      map[comp.id] = comp;
    });
    return map;
  }, [dynamicComponents]);

  const { content, tableContent } = useReplaceCMS({
    data: cmsPage,
    loading,
    replace: (domNode: any) => {
      if (domNode?.class === "h2") {
        const textH2 = getText(domNode);
        return (
          <Text type="headline-2" className="text-center mb-2">
            {textH2}
          </Text>
        );
      } else if (domNode.name === "h3") {
        return (
          <Text type="title-3" className="text-primary" htmlTag="h2">
            {domNode.children?.[0].data}
          </Text>
        );
      } else if (
        domNode.name === "div" &&
        domNode.attribs?.["data-component-id"]
      ) {
        // Render dynamic component
        const componentId = domNode.attribs["data-component-id"];
        const componentInfo = componentMap[componentId];

        if (componentInfo) {
          // Dynamically import and render the component
          const DynamicComponent = dynamic(
            () => import(`~/sections/${componentInfo.name}`),
            { ssr: false },
          );

          return <DynamicComponent {...componentInfo.props} />;
        }
      }
    },
  });

  const { meta_description, meta_title, meta_keywords, url_key, title } =
    data?.cmsPage || {};

  return (
    <Layout>
      <SEOTags
        title={meta_title}
        description={meta_description}
        keywords={meta_keywords}
        url={url_key}
      />
      {vars.template === "v1" ? (
        <Container
          rootClassName="py-10 lg:py-16 lg:pt-3"
          className="flex flex-col gap-8 cms-page"
        >
          <Breadcrumb
            items={[{ title: "Home", path: "/" }, { title: title }]}
          />
          <div className="flex flex-col-reverse gap-8 lg:flex-row">
            <div
              className="flex flex-col gap-8 "
              style={{ width: "-webkit-fill-available" }}
            >
              {data
                ? content
                : Array(5)
                    .fill("")
                    .map((_, index) => {
                      return (
                        <div key={index} className="flex flex-col gap-5">
                          <SkeletonByTimeout>
                            <Skeleton height={32} />
                          </SkeletonByTimeout>
                          <div className="flex flex-col gap-3">
                            {Array(3)
                              .fill("")
                              .map((_, cIndex) => {
                                return (
                                  <SkeletonByTimeout key={cIndex}>
                                    <Skeleton height={20} />
                                  </SkeletonByTimeout>
                                );
                              })}
                          </div>
                        </div>
                      );
                    })}
            </div>
            {!isEmpty(tableContent) && (
              <TableOfContent
                headings={tableContent.map((item) => item?.title)}
              />
            )}
          </div>
        </Container>
      ) : (
        <div className="flex flex-col-reverse gap-8 lg:flex-row">
          <div
            className="flex flex-col gap-8 "
            style={{ width: "-webkit-fill-available" }}
          >
            {data
              ? content
              : Array(5)
                  .fill("")
                  .map((_, index) => {
                    return (
                      <div key={index} className="flex flex-col gap-5">
                        <SkeletonByTimeout>
                          <Skeleton height={32} />
                        </SkeletonByTimeout>
                        <div className="flex flex-col gap-3">
                          {Array(3)
                            .fill("")
                            .map((_, cIndex) => {
                              return (
                                <SkeletonByTimeout key={cIndex}>
                                  <Skeleton height={20} />
                                </SkeletonByTimeout>
                              );
                            })}
                        </div>
                      </div>
                    );
                  })}
          </div>
          {!isEmpty(tableContent) && (
            <TableOfContent
              headings={tableContent.map((item) => item?.title)}
            />
          )}
        </div>
      )}
    </Layout>
  );
};

export default CmsPage;

export async function getServerSideProps({
  params,
}: GetServerSidePropsContext) {
  const { cmsPage: uri } = params || {};
  const apolloClient = initializeSSRApollo({
    httpLinkProps: { useGETForQueries: true },
  });

  let res = {} as any;
  const slug: string = (uri as string) || "";
  if (slug.includes("sneaker-release")) {
    return {
      notFound: true, // Prevent sneaker-release routes from being handled by this page
    };
  }
  try {
    const arrURL = slug?.split("-");
    const defaultIndex = arrURL?.length || 1;
    let detectSKU = arrURL[defaultIndex];
    for (let index = defaultIndex - 1; index >= 0; index--) {
      const regex = /^\d{12}(-p)?$/;

      if (regex.test(arrURL[index])) {
        if (index != arrURL?.length) {
          detectSKU =
            arrURL[index] + (arrURL[index + 1] ? `-${arrURL[index + 1]}` : "");
          break;
        }
        detectSKU = arrURL[index];
        break;
      }
    }
    if (!!detectSKU) {
      return {
        redirect: {
          destination: PATH.ProductDetail.replace("[slug]", slug),
          statusCode: 301,
        },
      };
    }
    if (!!uri) {
      res = await apolloClient.query({
        query: QUERY_GET_CMS_PAGE,
        variables: { identifier: uri },
        context: { fetchOptions: { method: "GET" } },
      });

      // Process CMS content to replace {{component}} and {{cms-block}} patterns
      if (res?.data?.cmsPage?.content) {
        const { processedContent, components, vars } = await processCMSContent(
          res.data.cmsPage.content,
          apolloClient,
        );

        // Create a new object with updated content instead of mutating
        const updatedData = {
          ...res.data,
          cmsPage: {
            ...res.data.cmsPage,
            content: processedContent,
          },
        };

        // Return components data and vars
        return {
          props: {
            uri,
            data: updatedData,
            networkStatus: res.networkStatus,
            components,
            vars,
          },
        };
      }
    }
  } catch (error: any) {
    // Only log unexpected errors, not "page doesn't exist" errors
    const isNotFoundError = error?.graphQLErrors?.some(
      (e: any) => e?.extensions?.category === "graphql-no-such-entity"
    );
    if (!isNotFoundError) {
      console.error(error, "QUERY_SEO_CATEGORY");
    }
    return {
      notFound: true,
    };
  }

  if (isEmpty(res)) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      uri,
      ...res,
      components: [],
      vars: {},
    },
  };
}
